import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Users, Search, Filter, X, Loader2, Download, Eye, 
  School, Coins, Calendar, BookOpen, ChevronDown, ChevronUp,
  Mail, Phone, MapPin, GraduationCap, Award, Clock, Trash2, Pencil
} from 'lucide-react';

export default function AdminLearnersPage({ embedded = false }) {
  const { api } = useAuth();
  const [learners, setLearners] = useState([]);
  const [filteredLearners, setFilteredLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [learnerDetails, setLearnerDetails] = useState(null);
  // Create / Edit learner dialog state
  const [learnerFormOpen, setLearnerFormOpen] = useState(false);
  const [editingLearner, setEditingLearner] = useState(null);
  const [learnerForm, setLearnerForm] = useState({
    parent: '', first_name: '', last_name: '', date_of_birth: '',
    grade: '', tshirt_size: '', allergies: '', medical_notes: '',
  });
  const [learnerSaving, setLearnerSaving] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    grade: 'all',
    partnerSchool: 'all',
    district: 'all',
    hasEnrollments: 'all',
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter options
  const [partnerSchools, setPartnerSchools] = useState([]);
  const [districts, setDistricts] = useState([]);
  
  // Sorting
  const [sortField, setSortField] = useState('last_name');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Enrollments data
  const [enrollmentsMap, setEnrollmentsMap] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [learners, searchQuery, filters, sortField, sortDirection]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [learnersRes, enrollmentsRes, partnerSchoolsRes] = await Promise.all([
        api.get('/learners/?page_size=5000'),
        api.get('/enrollments/?page_size=5000'),
        api.get('/partner/schools/').catch(() => ({ data: [] })),
      ]);

      const learnersData = learnersRes.data?.results || learnersRes.data || [];
      const enrollmentsData = enrollmentsRes.data?.results || enrollmentsRes.data || [];
      const schoolsData = partnerSchoolsRes.data?.results || partnerSchoolsRes.data || [];

      // Build enrollments map by learner
      const enrollMap = {};
      enrollmentsData.forEach(e => {
        const learnerId = e.learner || e.learner_id;
        if (!enrollMap[learnerId]) {
          enrollMap[learnerId] = [];
        }
        enrollMap[learnerId].push(e);
      });
      setEnrollmentsMap(enrollMap);

      // Extract unique districts
      const uniqueDistricts = [...new Set(learnersData.map(l => l.district_name).filter(Boolean))];
      setDistricts(uniqueDistricts);
      
      setPartnerSchools(schoolsData);
      setLearners(learnersData);
    } catch (error) {
      console.error('Failed to fetch learners:', error);
      toast.error('Failed to load learners');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let result = [...learners];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(l => 
        `${l.first_name} ${l.last_name}`.toLowerCase().includes(query) ||
        l.school?.toLowerCase().includes(query) ||
        l.district_name?.toLowerCase().includes(query) ||
        l.parent_email?.toLowerCase().includes(query)
      );
    }

    // Grade filter
    if (filters.grade !== 'all') {
      result = result.filter(l => l.grade === filters.grade);
    }

    // Partner school filter
    if (filters.partnerSchool !== 'all') {
      result = result.filter(l => 
        l.partner_school === filters.partnerSchool || 
        l.partner_school_id === filters.partnerSchool
      );
    }

    // District filter
    if (filters.district !== 'all') {
      result = result.filter(l => l.district_name === filters.district);
    }

    // Has enrollments filter
    if (filters.hasEnrollments !== 'all') {
      if (filters.hasEnrollments === 'yes') {
        result = result.filter(l => (enrollmentsMap[l.id]?.length || 0) > 0);
      } else {
        result = result.filter(l => (enrollmentsMap[l.id]?.length || 0) === 0);
      }
    }

    // Sort
    result.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'name':
          aVal = `${a.last_name} ${a.first_name}`.toLowerCase();
          bVal = `${b.last_name} ${b.first_name}`.toLowerCase();
          break;
        case 'grade':
          aVal = a.grade === 'K' ? 0 : parseInt(a.grade) || 99;
          bVal = b.grade === 'K' ? 0 : parseInt(b.grade) || 99;
          break;
        case 'points':
          aVal = a.points_balance || 0;
          bVal = b.points_balance || 0;
          break;
        case 'enrollments':
          aVal = enrollmentsMap[a.id]?.length || 0;
          bVal = enrollmentsMap[b.id]?.length || 0;
          break;
        default:
          aVal = a[sortField]?.toString().toLowerCase() || '';
          bVal = b[sortField]?.toString().toLowerCase() || '';
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    setFilteredLearners(result);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUp className="h-4 w-4 inline ml-1" /> : 
      <ChevronDown className="h-4 w-4 inline ml-1" />;
  };

  const viewLearnerDetails = async (learner) => {
    setSelectedLearner(learner);
    setDetailsOpen(true);
    setDetailsLoading(true);
    
    try {
      // Fetch additional details if needed
      const enrollments = enrollmentsMap[learner.id] || [];
      
      // Get parent info
      let parentInfo = null;
      if (learner.parent) {
        try {
          const parentRes = await api.get(`/users/${learner.parent}/`);
          parentInfo = parentRes.data;
        } catch (e) {
          console.log('Could not fetch parent info');
        }
      }

      setLearnerDetails({
        ...learner,
        enrollments,
        parentInfo,
      });
    } catch (error) {
      console.error('Failed to fetch learner details:', error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      grade: 'all',
      partnerSchool: 'all',
      district: 'all',
      hasEnrollments: 'all',
    });
    setSearchQuery('');
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== 'all').length + (searchQuery ? 1 : 0);

  const exportLearners = () => {
    const data = [
      ['First Name', 'Last Name', 'Grade', 'School', 'District', 'Partner School', 'Points Balance', 'Enrollments', 'Parent Email', 'Created Date']
    ];

    filteredLearners.forEach(l => {
      const partnerSchool = partnerSchools.find(s => s.id === l.partner_school || s.id === l.partner_school_id);
      data.push([
        l.first_name || '',
        l.last_name || '',
        l.grade || '',
        l.school || '',
        l.district_name || '',
        partnerSchool?.name || '',
        l.points_balance || 0,
        enrollmentsMap[l.id]?.length || 0,
        l.parent_email || '',
        l.created_at ? new Date(l.created_at).toLocaleDateString() : ''
      ]);
    });

    const csvContent = data.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `learners_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Learners exported successfully!');
  };

  // ─── Create / Edit learner ────────────────────────────────────────
  // Admin-only flow: create children on behalf of a parent (e.g. over
  // the phone) or tune fields when a student re-enrolls next year.
  const openEditLearner = (l) => {
    setEditingLearner(l);
    setLearnerForm({
      parent: l.parent || '',
      first_name: l.first_name || '',
      last_name: l.last_name || '',
      date_of_birth: l.date_of_birth || '',
      grade: l.grade || '',
      tshirt_size: l.tshirt_size || '',
      allergies: l.allergies || '',
      medical_notes: l.medical_notes || '',
    });
    setLearnerFormOpen(true);
  };

  const saveLearner = async () => {
    if (!learnerForm.first_name || !learnerForm.last_name || !learnerForm.grade) {
      toast.error('First name, last name, and grade are required');
      return;
    }
    if (!editingLearner && !learnerForm.parent) {
      toast.error('Parent (user UUID or email) is required when creating a learner');
      return;
    }
    setLearnerSaving(true);
    try {
      if (editingLearner) {
        const { parent: _p, ...patch } = learnerForm; // don't reassign parent on edit
        await api.patch(`/learners/${editingLearner.id}/`, patch);
        toast.success('Learner updated');
      } else {
        await api.post('/learners/', learnerForm);
        toast.success('Learner created');
      }
      setLearnerFormOpen(false);
      // Refetch the full list from /learners/
      const { data } = await api.get('/learners/?page_size=5000');
      setLearners(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      toast.error(
        err.response?.data?.detail
        || Object.values(err.response?.data || {})[0]?.[0]
        || 'Save failed'
      );
    } finally {
      setLearnerSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-learners-page">
      {!embedded ? (
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold">Learners</h1>
            <p className="text-muted-foreground">View and manage all learners enrolled in Ready Set STEM courses</p>
          </div>
          <Button onClick={() => { setLearnerForm({ parent: '', first_name: '', last_name: '', date_of_birth: '', grade: '', tshirt_size: '', allergies: '', medical_notes: '' }); setEditingLearner(null); setLearnerFormOpen(true); }} data-testid="new-learner-btn">
            <Users className="h-4 w-4 mr-1" /> New learner
          </Button>
        </div>
      ) : (
        <div className="flex justify-end">
          <Button onClick={() => { setLearnerForm({ parent: '', first_name: '', last_name: '', date_of_birth: '', grade: '', tshirt_size: '', allergies: '', medical_notes: '' }); setEditingLearner(null); setLearnerFormOpen(true); }} data-testid="new-learner-btn">
            <Users className="h-4 w-4 mr-1" /> New learner
          </Button>
        </div>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{learners.length}</p>
                <p className="text-sm text-muted-foreground">Total Learners</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <School className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold">{learners.filter(l => l.partner_school || l.partner_school_id).length}</p>
                <p className="text-sm text-muted-foreground">With Partner School</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold">{learners.filter(l => (enrollmentsMap[l.id]?.length || 0) > 0).length}</p>
                <p className="text-sm text-muted-foreground">With Enrollments</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Coins className="h-8 w-8 text-amber-600" />
              <div>
                <p className="text-2xl font-bold">{Math.round(learners.reduce((sum, l) => sum + (l.points_balance || 0), 0) / (learners.length || 1))}</p>
                <p className="text-sm text-muted-foreground">Avg Points</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, school, district, or parent email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="learner-search"
              />
            </div>
            
            {/* Filter Toggle */}
            <Button 
              variant={showFilters ? "default" : "outline"}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <Badge className="ml-2 bg-white text-primary">{activeFilterCount}</Badge>
              )}
            </Button>
            
            {/* Export */}
            <Button variant="outline" onClick={exportLearners}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Grade */}
              <div className="space-y-2">
                <Label>Grade</Label>
                <Select value={filters.grade} onValueChange={(v) => setFilters({...filters, grade: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All grades" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Grades</SelectItem>
                    <SelectItem value="K">Kindergarten</SelectItem>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(g => (
                      <SelectItem key={g} value={g.toString()}>Grade {g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Partner School */}
              <div className="space-y-2">
                <Label>Partner School</Label>
                <Select value={filters.partnerSchool} onValueChange={(v) => setFilters({...filters, partnerSchool: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All schools" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Partner Schools</SelectItem>
                    {partnerSchools.map(school => (
                      <SelectItem key={school.id} value={school.id}>{school.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* District */}
              <div className="space-y-2">
                <Label>District</Label>
                <Select value={filters.district} onValueChange={(v) => setFilters({...filters, district: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All districts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Districts</SelectItem>
                    {districts.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Has Enrollments */}
              <div className="space-y-2">
                <Label>Enrollment Status</Label>
                <Select value={filters.hasEnrollments} onValueChange={(v) => setFilters({...filters, hasEnrollments: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Learners</SelectItem>
                    <SelectItem value="yes">With Enrollments</SelectItem>
                    <SelectItem value="no">No Enrollments</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {activeFilterCount > 0 && (
                <div className="md:col-span-4 flex justify-end">
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="h-4 w-4 mr-1" />
                    Clear All Filters
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {filteredLearners.length} of {learners.length} learners
        </p>
      </div>

      {/* Learners Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('name')}
                >
                  Name <SortIcon field="name" />
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('grade')}
                >
                  Grade <SortIcon field="grade" />
                </TableHead>
                <TableHead>School / District</TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 text-center"
                  onClick={() => handleSort('enrollments')}
                >
                  Enrollments <SortIcon field="enrollments" />
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 text-center"
                  onClick={() => handleSort('points')}
                >
                  Points <SortIcon field="points" />
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLearners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No learners found matching your criteria
                  </TableCell>
                </TableRow>
              ) : (
                filteredLearners.map((learner, index) => {
                  const enrollmentCount = enrollmentsMap[learner.id]?.length || 0;
                  const partnerSchool = partnerSchools.find(s => s.id === learner.partner_school || s.id === learner.partner_school_id);
                  
                  return (
                    <TableRow key={learner.id} data-testid={`learner-row-${index}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{learner.first_name} {learner.last_name}</p>
                          {learner.parent_email && (
                            <p className="text-xs text-muted-foreground">{learner.parent_email}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {learner.grade === 'K' ? 'Kindergarten' : `Grade ${learner.grade}`}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {partnerSchool ? (
                            <div className="flex items-center gap-1">
                              <School className="h-3 w-3 text-blue-600" />
                              <span className="text-sm">{partnerSchool.name}</span>
                              <Badge variant="secondary" className="text-xs ml-1">Partner</Badge>
                            </div>
                          ) : learner.school ? (
                            <span className="text-sm">{learner.school}</span>
                          ) : (
                            <span className="text-sm text-muted-foreground">No school</span>
                          )}
                          {learner.district_name && (
                            <p className="text-xs text-muted-foreground">{learner.district_name}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={enrollmentCount > 0 ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}>
                          {enrollmentCount}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Coins className="h-4 w-4 text-amber-500" />
                          <span className="font-medium">{learner.points_balance || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => viewLearnerDetails(learner)}
                          data-testid={`view-learner-${index}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => openEditLearner(learner)}
                          data-testid={`edit-learner-${index}`}
                          title="Edit learner details"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Learner Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Learner Details
            </DialogTitle>
          </DialogHeader>
          
          {detailsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : learnerDetails && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="bg-muted/50 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-3">
                  {learnerDetails.first_name} {learnerDetails.last_name}
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <GraduationCap className="h-4 w-4 text-muted-foreground" />
                    <span>{learnerDetails.grade === 'K' ? 'Kindergarten' : `Grade ${learnerDetails.grade}`}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Coins className="h-4 w-4 text-amber-500" />
                    <span>{learnerDetails.points_balance || 0} points</span>
                  </div>
                  {learnerDetails.school && (
                    <div className="flex items-center gap-2 col-span-2">
                      <School className="h-4 w-4 text-muted-foreground" />
                      <span>{learnerDetails.school}</span>
                    </div>
                  )}
                  {learnerDetails.district_name && (
                    <div className="flex items-center gap-2 col-span-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>{learnerDetails.district_name}</span>
                    </div>
                  )}
                  {learnerDetails.created_at && (
                    <div className="flex items-center gap-2 col-span-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span>Registered: {new Date(learnerDetails.created_at).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Parent Info */}
              {learnerDetails.parentInfo && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Parent/Guardian
                  </h4>
                  <Card>
                    <CardContent className="p-4">
                      <p className="font-medium">{learnerDetails.parentInfo.first_name} {learnerDetails.parentInfo.last_name}</p>
                      <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Mail className="h-3 w-3" />
                          {learnerDetails.parentInfo.email}
                        </div>
                        {learnerDetails.parentInfo.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-3 w-3" />
                            {learnerDetails.parentInfo.phone}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Enrollment History */}
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Enrollment History ({learnerDetails.enrollments?.length || 0})
                </h4>
                {learnerDetails.enrollments?.length === 0 ? (
                  <Card>
                    <CardContent className="p-4 text-center text-muted-foreground">
                      No enrollments yet
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {learnerDetails.enrollments?.map((enrollment, idx) => (
                      <Card key={idx}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">{enrollment.section_title || enrollment.course_title || 'Course'}</p>
                              <p className="text-sm text-muted-foreground">
                                {enrollment.location_name || 'Location TBD'}
                              </p>
                              {enrollment.created_at && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Enrolled: {new Date(enrollment.created_at).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                            <Badge className={
                              enrollment.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                              enrollment.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                              enrollment.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }>
                              {enrollment.status}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Additional Info */}
              {(learnerDetails.shirt_size || learnerDetails.allergies || learnerDetails.notes) && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Award className="h-4 w-4" />
                    Additional Information
                  </h4>
                  <Card>
                    <CardContent className="p-4 space-y-2 text-sm">
                      {learnerDetails.shirt_size && (
                        <p><span className="text-muted-foreground">Shirt Size:</span> {learnerDetails.shirt_size}</p>
                      )}
                      {learnerDetails.allergies && (
                        <p><span className="text-muted-foreground">Allergies:</span> {learnerDetails.allergies}</p>
                      )}
                      {learnerDetails.notes && (
                        <p><span className="text-muted-foreground">Notes:</span> {learnerDetails.notes}</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Remove Learner */}
              <div className="pt-4 border-t">
                <Button variant="destructive" className="w-full" onClick={async () => {
                  if (!window.confirm(`Remove ${learnerDetails.first_name} ${learnerDetails.last_name}? This will also delete all their enrollments. This cannot be undone.`)) return;
                  try {
                    await api.delete(`/learners/${learnerDetails.id}/`);
                    toast.success(`${learnerDetails.first_name} ${learnerDetails.last_name} removed`);
                    setDetailsOpen(false);
                    fetchData();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to remove learner');
                  }
                }}>
                  <Trash2 className="h-4 w-4 mr-2" /> Remove Learner
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create / Edit Learner dialog — admin-only flow for on-behalf edits. */}
      <Dialog open={learnerFormOpen} onOpenChange={setLearnerFormOpen}>
        <DialogContent className="max-w-xl" data-testid="learner-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingLearner ? 'Edit learner' : 'New learner'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {!editingLearner && (
              <div className="col-span-2">
                <Label>Parent (user UUID)</Label>
                <Input
                  value={learnerForm.parent}
                  onChange={e => setLearnerForm(p => ({ ...p, parent: e.target.value }))}
                  placeholder="aa11bb22-…"
                  data-testid="learner-parent-input"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Paste the parent's user UUID from the Users admin.
                </p>
              </div>
            )}
            <div>
              <Label>First name</Label>
              <Input value={learnerForm.first_name} onChange={e => setLearnerForm(p => ({ ...p, first_name: e.target.value }))} data-testid="learner-first-name" />
            </div>
            <div>
              <Label>Last name</Label>
              <Input value={learnerForm.last_name} onChange={e => setLearnerForm(p => ({ ...p, last_name: e.target.value }))} data-testid="learner-last-name" />
            </div>
            <div>
              <Label>Date of birth</Label>
              <Input type="date" value={learnerForm.date_of_birth} onChange={e => setLearnerForm(p => ({ ...p, date_of_birth: e.target.value }))} data-testid="learner-dob" />
            </div>
            <div>
              <Label>Grade</Label>
              <Input value={learnerForm.grade} onChange={e => setLearnerForm(p => ({ ...p, grade: e.target.value }))} placeholder="K, 1, 2, …, 8" data-testid="learner-grade" />
            </div>
            <div>
              <Label>T-shirt size</Label>
              <Input value={learnerForm.tshirt_size} onChange={e => setLearnerForm(p => ({ ...p, tshirt_size: e.target.value }))} placeholder="YS / YM / YL / AS" />
            </div>
            <div className="col-span-2">
              <Label>Allergies</Label>
              <Input value={learnerForm.allergies} onChange={e => setLearnerForm(p => ({ ...p, allergies: e.target.value }))} />
            </div>
            <div className="col-span-2">
              <Label>Medical notes</Label>
              <Input value={learnerForm.medical_notes} onChange={e => setLearnerForm(p => ({ ...p, medical_notes: e.target.value }))} />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setLearnerFormOpen(false)}>Cancel</Button>
            <Button onClick={saveLearner} disabled={learnerSaving} data-testid="save-learner-btn">
              {learnerSaving && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
              {editingLearner ? 'Save changes' : 'Create learner'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
